import { getCameraId } from "../../../shared/utils/utils";
import { STATUS_OPTIONS } from "../../../shared/utils/utils";
import { getAzureBlob, saveContainerWeight } from "../../../services/container";
import moment from "moment";
import i18n from "i18next";
import { formattedTime } from "../../../components/formatted/FormattedDateAndTime";

/**
 * Drop-off / pick-up datetime for activity rows.
 * Example FR: "18 septembre 2026 à 17h12"
 */
export const formatActivityDateTime = (date, t) => {
  if (!date) return "...";

  const language = i18n.language || "fr-FR";
  let datePart = new Date(date)
    .toLocaleDateString(language, {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .replace(/\bde\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  datePart = datePart.replace(/^\p{L}/u, (char) => char.toUpperCase());

  return `${datePart} ${t("à")} ${formattedTime(date)}`;
};

/**
 * Utilitaires pour ActivityCollectionRow
 */

const STATUS_KEYS = ["done", "none", "interrogation", "warning"];

const MARK_AS_STATUS_VALUES = ["done", "none", "warning"];

const STATUS_LABEL_ALIASES = {
  done: [
    "done",
    "verified",
    "validated",
    "verifie",
    "vérifié",
    "verificado",
    "validerad",
    "validé",
    "valider",
    "validado",
  ],
  none: [
    "none",
    "no status",
    "pas de statut",
    "sin estado",
    "ingen status",
    "to process",
    "check",
    "a traiter",
    "à traiter",
    "pointer",
    "a procesar",
    "marcar",
    "att behandla",
    "bocka",
    "prüfen",
  ],
  interrogation: ["interrogation", "interrogación", "förfrågan"],
  warning: ["warning", "avertissement", "advertencia", "varning"],
};

const STATUS_ICON_ALIASES = {
  "fas fa-check-circle": "done",
  "fas fa-exclamation-circle": "warning",
  "fas fa-question-circle": "interrogation",
  "fas fa-minus-circle": "none",
};

const STATUS_COLOR_ALIASES = {
  "text-green-600": "done",
  "text-amber-500": "warning",
  "text-red-500": "interrogation",
  "text-gray-400": "none",
};

const parseCheckStatus = (checkStatus) => {
  let current = checkStatus;

  for (let depth = 0; depth < 3; depth += 1) {
    if (current == null || current === "" || current === "-") {
      return null;
    }

    if (typeof current === "object") {
      return current;
    }

    if (typeof current !== "string") {
      return null;
    }

    const trimmed = current.trim();
    if (!trimmed) {
      return null;
    }

    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
      return trimmed;
    }

    try {
      current = JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }

  return typeof current === "object" ? current : current;
};

const resolveStatusKeyFromIcon = (icon) => {
  if (!icon || typeof icon !== "string") {
    return null;
  }

  if (STATUS_ICON_ALIASES[icon]) {
    return STATUS_ICON_ALIASES[icon];
  }

  if (icon.includes("check-circle")) {
    return "done";
  }

  if (icon.includes("exclamation")) {
    return "warning";
  }

  if (icon.includes("question")) {
    return "interrogation";
  }

  if (icon.includes("minus")) {
    return "none";
  }

  return null;
};

const resolveStatusKeyFromString = (rawValue) => {
  const normalized = String(rawValue).trim().toLowerCase();
  if (!normalized) return null;

  if (STATUS_KEYS.includes(normalized)) {
    return normalized;
  }

  const fromIcon = resolveStatusKeyFromIcon(String(rawValue).trim());
  if (fromIcon) {
    return fromIcon;
  }

  return (
    Object.entries(STATUS_LABEL_ALIASES).find(([, aliases]) =>
      aliases.includes(normalized),
    )?.[0] ?? null
  );
};

const resolveStatusKeyFromObject = (statusObject) => {
  const candidateValues = [
    statusObject.key,
    statusObject.value,
    statusObject.label,
  ];

  for (const candidate of candidateValues) {
    if (candidate == null || candidate === "") continue;
    const resolved = resolveStatusKeyFromString(candidate);
    if (resolved) return resolved;
  }

  const fromIcon = resolveStatusKeyFromIcon(statusObject.icon);
  if (fromIcon) {
    return fromIcon;
  }

  if (statusObject.color && STATUS_COLOR_ALIASES[statusObject.color]) {
    return STATUS_COLOR_ALIASES[statusObject.color];
  }

  return null;
};

export const getRawStoredCheckStatus = (rawCheckStatus) => {
  if (
    rawCheckStatus == null ||
    rawCheckStatus === "" ||
    rawCheckStatus === "-"
  ) {
    return "";
  }

  const parsed = parseCheckStatus(rawCheckStatus);

  if (parsed && typeof parsed === "object") {
    const rawField = parsed.key ?? parsed.value;
    if (rawField != null && String(rawField).trim() !== "") {
      return String(rawField).trim();
    }
    return "";
  }

  if (typeof parsed === "string") {
    return parsed.trim();
  }

  return "";
};

/** V2 pill mapping: empty / "No status" → none (Pointer), resolve legacy objects. */
export const normalizeStatusKey = (checkStatus) => {
  const parsed = parseCheckStatus(checkStatus);
  if (parsed === null) return "none";

  if (typeof parsed === "string") {
    return resolveStatusKeyFromString(parsed) ?? "none";
  }

  if (typeof parsed !== "object") {
    return "none";
  }

  return resolveStatusKeyFromObject(parsed) ?? "none";
};

const V2_STATUS_LABEL_KEYS = {
  done: "Validated",
  none: "To process",
  interrogation: "Interrogation",
  warning: "Warning",
};

/** Mark-as menu entries (v2 labels), excluding the current status. */
export const getMarkAsMenuOptions = (options, currentKey, t) =>
  options
    .filter(
      (option) =>
        MARK_AS_STATUS_VALUES.includes(option.value) &&
        option.value !== currentKey,
    )
    .map((option) => ({
      ...option,
      label: t(V2_STATUS_LABEL_KEYS[option.value] || option.label),
    }));

export const resolveStatusPresentation = (
  checkStatus,
  t,
  rawCheckStatus = null,
) => {
  const options = STATUS_OPTIONS(t);
  const resolvedKey = normalizeStatusKey(checkStatus);
  const option =
    options.find((item) => item.value === resolvedKey) ||
    options.find((item) => item.value === "none") ||
    options[0];
  const storedRawValue = getRawStoredCheckStatus(rawCheckStatus);
  const labelKey = V2_STATUS_LABEL_KEYS[option.value];

  return {
    key: option.value,
    label: labelKey ? t(labelKey) : option.label,
    storedRawValue,
    statusIcon: option.statusIcon,
    pillBackground: option.pillBackground,
    pillTextColor: option.pillTextColor,
  };
};

export const buildStatusObject = (value, t) => {
  const found =
    STATUS_OPTIONS(t).find((o) => o.value === value) || STATUS_OPTIONS(t)[3];
  return { value: found.value, label: found.label, icon: found.icon };
};

const buildClientBlobName = (timeLine, superContainers, imageData) => {
  const cameraId = getCameraId(imageData.image_url);
  const id =
    superContainers &&
    superContainers.containerObjectIdList?.includes(timeLine._id)
      ? superContainers._id
      : timeLine._id;

  return `camera-ak_${cameraId}/output-client/${timeLine._time.replace(/:/g, "_").slice(0, -1)}-camera-ak_${cameraId}-${id}-client.json`;
};

export const getWeightDisplayValue = (
  clientRegisteredWeight,
  timeLine,
  historyCycle,
  weightToTonnes,
  toFrenchNumber,
  t,
) => {
  if (clientRegisteredWeight) {
    return `${clientRegisteredWeight} t`;
  }
  if (historyCycle.length === 1 || timeLine.density === 0) {
    return t("pleaseEnterWeight");
  }

  const estimatedWeight = toFrenchNumber(
    historyCycle[0].mass < 1000
      ? historyCycle[0].mass
      : historyCycle[0].mass / 1000,
    1,
  );

  const unit = historyCycle[0].mass < 1000 ? "kg" : "t";

  return `${estimatedWeight} ${unit}`;
};

export const patchClient = async ({
  timeLine,
  superContainers,
  imageData,
  patch,
  azureUrl,
}) => {
  const blobName = buildClientBlobName(timeLine, superContainers, imageData);
  const blobUrl = azureUrl + blobName;

  try {
    const azureResp = await getAzureBlob({ blobUrl });
    const base = azureResp.data || {};
    const clientObj = base.client ? JSON.parse(base.client) : {};

    const next = {
      ...base,
      modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
      client: JSON.stringify({ ...clientObj, ...patch }),
      blobName,
    };

    await saveContainerWeight(next);
    return;
  } catch (e) {
    if (e?.response?.status === 404) {
      const id =
        superContainers &&
        superContainers.containerObjectIdList?.includes(timeLine._id)
          ? superContainers._id
          : timeLine._id;

      const next = {
        id,
        time: timeLine._time,
        modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
        client: JSON.stringify({ ...patch }),
        blobName,
      };
      await saveContainerWeight(next);
      return;
    }
    throw e;
  }
};

export const ACTIVITY_DETAIL_STORAGE_KEY = "activityCollectionDetail";

export const resolveActiveWasteCategoryFromName = (
  wasteCategoryName,
  fallback = "",
) => {
  if (
    !wasteCategoryName ||
    wasteCategoryName === "" ||
    wasteCategoryName === "{}"
  ) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(wasteCategoryName);
    return parsed?.wasteCategoryName || fallback;
  } catch {
    return fallback;
  }
};

/** Stream label for activity views/exports: surface label, then client waste_category, then container flux. */
export const resolveActivityFluxLabel = ({
  wasteCategoryLabel,
  wasteCategoryJson,
  containerWasteName = "",
}) => {
  const trimmedLabel =
    typeof wasteCategoryLabel === "string" ? wasteCategoryLabel.trim() : "";
  if (trimmedLabel) {
    return trimmedLabel;
  }

  return resolveActiveWasteCategoryFromName(
    wasteCategoryJson,
    containerWasteName,
  );
};

export const readActivityDetailNavigationState = (location) => {
  if (location.state?.timeLine) {
    try {
      sessionStorage.setItem(
        ACTIVITY_DETAIL_STORAGE_KEY,
        JSON.stringify(location.state),
      );
    } catch {
      // ignore storage errors
    }
    return location.state;
  }

  try {
    const stored = sessionStorage.getItem(ACTIVITY_DETAIL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};
